import React, { useState, useCallback, useEffect } from 'react';
import { toastSuccess, toastError } from '../../../shared/toastHelper';
import MembershipCard from './MembershipCard';
import MembershipsDatatable from '../../Memberships/components/MembershipsDatatable';

const TAB_PRODUCTS = 'products';
const TAB_MEMBERS = 'members';

function readTabFromUrl() {
  if (typeof window === 'undefined') return TAB_PRODUCTS;
  const params = new URLSearchParams(window.location.search);
  const v = params.get('tab');
  return v === TAB_MEMBERS ? TAB_MEMBERS : TAB_PRODUCTS;
}

export default function MembershipsTabs({
  csrfToken,
  team,
  publicMembershipsUrl,
  publicMembershipsWidgetUrl,
  datatablePath,
  membershipProducts: initialMembershipProducts,
  membershipsFilters,
}) {
  const [tab, setTab] = useState(readTabFromUrl());
  const [membershipProducts, setMembershipProducts] = useState(initialMembershipProducts);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (tab === TAB_PRODUCTS) {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    const qs = params.toString();
    const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
    window.history.replaceState(null, '', nextUrl);
  }, [tab]);

  const go = useCallback((next) => () => setTab(next), []);

  const handleDelete = async (membershipProduct) => {
    try {
      const response = await fetch(
        `/teams/${team.id}/membership_products/${membershipProduct.id}/archive`,
        {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        setMembershipProducts((prev) =>
          prev.filter((mp) => mp.id !== membershipProduct.id)
        );
        toastSuccess('Membership archived successfully.');
      } else {
        toastError('Failed to archive membership. Please try again.');
      }
    } catch {
      toastError('Failed to archive membership. Please try again.');
    }
  };

  const handleStatusChange = async (membershipProduct, newStatus) => {
    const action = newStatus === 'active' ? 'publish' : 'unpublish';
    const pastLabel = newStatus === 'active' ? 'published' : 'unpublished';
    try {
      const response = await fetch(
        `/teams/${team.id}/membership_products/${membershipProduct.id}/${action}`,
        {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        setMembershipProducts((prev) =>
          prev.map((mp) =>
            mp.id === membershipProduct.id ? { ...mp, status: newStatus } : mp
          )
        );
        toastSuccess(`Membership ${pastLabel} successfully.`);
      } else {
        toastError(`Failed to ${action} membership. Please try again.`);
      }
    } catch {
      toastError(`Failed to ${action} membership. Please try again.`);
    }
  };

  return (
    <div className="memberships-tabs">
      <ul className="nav nav-pills small">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link text-dark ${tab === TAB_PRODUCTS ? 'active' : ''}`}
            style={{ border: 'none', background: tab === TAB_PRODUCTS ? undefined : 'transparent' }}
            onClick={go(TAB_PRODUCTS)}
          >
            Products
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link text-dark ${tab === TAB_MEMBERS ? 'active' : ''}`}
            style={{ border: 'none', background: tab === TAB_MEMBERS ? undefined : 'transparent' }}
            onClick={go(TAB_MEMBERS)}
          >
            Members
          </button>
        </li>
      </ul>
      <div className="tab-content py-3">
        {tab === TAB_PRODUCTS && (
          <MembershipCard
            membershipProducts={membershipProducts}
            team={team}
            publicMembershipsUrl={publicMembershipsUrl}
            publicMembershipsWidgetUrl={publicMembershipsWidgetUrl}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
          />
        )}
        {tab === TAB_MEMBERS && (
          <div className="team-member-card">
            <div className="team-member-card-header">
              <div className="team-member-card-header-left">
                <div className="team-member-card-title-row">
                  <h2 className="team-member-card-title">Members</h2>
                </div>
                <p className="team-member-card-subtitle">
                  All active memberships across your products.
                </p>
              </div>
            </div>
            <MembershipsDatatable
              datatablePath={datatablePath}
              filters={membershipsFilters}
              showProductColumn
            />
          </div>
        )}
      </div>
    </div>
  );
}
